import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DailyDiagramInterface,
  DailyDiagramListItemInterface,
  DispatcherStateType,
  GetDeviceProductionPlanDetailResponse,
  MonitoredResourceType,
  ActivityLogInterface,
  SetIntoTimeSeriesPayloadBatch,
  TimeSeriesInterface,
  IsDispatcherReadyInterface,
  GetProductionPlansBatchResponse
} from './dispatcherTypes';
import { CaCodeType, ResourceContextAction } from 'models/contextActions';
import { mergeContextActions } from 'utils/contextActionsUtils';
import { EtagInterface } from 'models/etag';
import {
  canMetricHaveMultipleValuesForInterval,
  mergeDataPoints,
  hasSameLabels
} from 'views/modules/Dispatcher/OnlineDispatching/Utils/metricUtils';
import { ReservedCapacityGuardingAction } from './dispatcherReservedCapacityGuardingTypes';
import { AlarmDto } from 'views/modules/System/Alarms/alarmsModels';
import { FetchDeliveryPointListResponse } from 'features/eai/eaiTypes';

// ==============================|| DISPATCHER SLICE ||============================== //

const initialIsReady: IsDispatcherReadyInterface = {
  isDailyDiagramReady: false,
  isProductionPlanListReady: false,
  isDeviceMetricListReady: false,
  isMetricListForBalanceGroupChartReady: false,
  isMetricListForSystemValuesChartReady: false
};

export const initialState: DispatcherStateType = {
  alarms: [],
  codebooks: [],
  marketHours: [],
  marketPeriods: [],
  selectedBusinessDay: '',
  contextActions: [],
  deviceProductionPlanListForOnlineDispatching: [],
  deviceProductionPlanListForPlanning: [],
  sseToken: undefined,
  metricSpecs: [],
  operationCalendar: {
    calendarList: undefined,
    events: undefined,
    eventDetail: undefined,
    activityEvents: undefined
  },
  etag: {},
  timeSeriesListForOnlineDispatching: [],
  timeSeriesListForPlanning: {
    SYSTEM: [],
    BALANCING_GROUP: [],
    DELIVERY_POINT: [],
    DEVICE: []
  },
  timeSeriesListForHx: {
    BALANCING_GROUP: [],
    DEVICE: [],
    SYSTEM: []
  },
  timeSeriesListForReservedCapacityGuarding: {
    DELIVERY_POINT: [],
    DEVICE: []
  },
  dailyDiagram: undefined,
  dailyDiagramList: [],
  balancingGroup: {
    deliveryPointList: []
  },
  timeSeries: undefined,
  productionPlanActivityLog: [],
  dailyDiagramActivityLog: [],
  calendarActivityLog: [],
  activityLogs: [],
  isReady: initialIsReady,
  reservedCapacityGuardingActions: [],
  isReservedCapacityGuardingActionsReady: false
};

const getTimeSeries = (
  itemsToAdd: TimeSeriesInterface[],
  action: PayloadAction<SetIntoTimeSeriesPayloadBatch>,
  monitoredResourceType?: MonitoredResourceType
): TimeSeriesInterface[] => {
  const {
    ignoreMetricLabels,
    overwriteOldDataPoints,
    setIntoTimeSeriesPayload
  } = action.payload;

  setIntoTimeSeriesPayload?.forEach((timeSeriesItem) => {
    const { data, resource } = timeSeriesItem;

    if (!data || !resource) {
      return;
    }

    if (
      monitoredResourceType &&
      monitoredResourceType !== resource.resourceType
    ) {
      return;
    }

    let existingTimeSeriesItemProcessed = false;
    for (let i = 0; i < itemsToAdd.length; i++) {
      const newTimeSeriesItem = itemsToAdd[i];

      const isSameMonitoredResourceType =
        newTimeSeriesItem.monitoredResource.type === resource.resourceType;
      const isSameMetricType =
        newTimeSeriesItem.metric.type === data.metric.type;
      const hasSameMetricLabels =
        ignoreMetricLabels ??
        hasSameLabels(newTimeSeriesItem.metric.labels, data.metric.labels);
      const hasSameMonitoredResourceLabels = hasSameLabels(
        newTimeSeriesItem.monitoredResource.labels,
        data.monitoredResource.labels
      );

      const isExitingTimeSeries =
        isSameMonitoredResourceType &&
        isSameMetricType &&
        hasSameMetricLabels &&
        hasSameMonitoredResourceLabels;

      if (isExitingTimeSeries) {
        const newDataPoints = overwriteOldDataPoints
          ? data.dataPoints
          : mergeDataPoints(
              newTimeSeriesItem?.dataPoints || [],
              data.dataPoints || [],
              canMetricHaveMultipleValuesForInterval(
                newTimeSeriesItem.metric.type
              )
            );

        itemsToAdd[i] = {
          ...newTimeSeriesItem,
          dataPoints: newDataPoints
        };
        existingTimeSeriesItemProcessed = true;
        break;
      }
    }

    if (!existingTimeSeriesItemProcessed && timeSeriesItem.data) {
      itemsToAdd.push(timeSeriesItem.data);
    }
  });

  return itemsToAdd;
};

const dispatcherSlice = createSlice({
  name: 'dispatcher',
  initialState,
  reducers: {
    clearDispatcherState() {
      return initialState;
    },
    setCodebooks(state, action: PayloadAction<any>) {
      state.codebooks = action.payload;
    },
    setDispatcherContextActions(
      state,
      action: PayloadAction<ResourceContextAction[] | undefined>
    ) {
      if (action.payload?.length) {
        state.contextActions = mergeContextActions(
          state.contextActions,
          action.payload
        );
      }
    },
    removeDispatcherContextActions(
      state,
      action: PayloadAction<{ caList: CaCodeType[] }>
    ) {
      state.contextActions = state.contextActions.map((contextAction) => ({
        ...contextAction,
        contextActions: contextAction.contextActions.filter(
          (item) => action.payload.caList.indexOf(item.code) === -1
        )
      }));
    },
    setIntoDeviceProductionPlanForOnlineDispatching: (
      state,
      action: PayloadAction<GetDeviceProductionPlanDetailResponse | undefined>
    ) => {
      state.deviceProductionPlanListForOnlineDispatching = action.payload
        ? [
            ...state.deviceProductionPlanListForOnlineDispatching.filter(
              (plan) => plan.deviceId !== action.payload?.deviceId
            ),
            action.payload
          ]
        : [];
    },
    setIntoDeviceProductionPlanForPlanning: (
      state,
      action: PayloadAction<GetDeviceProductionPlanDetailResponse | undefined>
    ) => {
      state.deviceProductionPlanListForPlanning = action.payload
        ? [
            ...state.deviceProductionPlanListForPlanning.filter(
              (plan) => plan.deviceId !== action.payload?.deviceId
            ),
            action.payload
          ]
        : [];
    },
    setPlansIntoDeviceProductionPlansBatchForOnlineDispatching: (
      state,
      action: PayloadAction<GetProductionPlansBatchResponse>
    ) => {
      const plans = action.payload._embedded?.productionPlanDetailDtoList;

      if (!plans || plans.length === 0) return;

      const deviceIds = plans.map(
        (device: GetDeviceProductionPlanDetailResponse) => device.deviceId
      );

      const plansFromStateWithoutNewPlans = [
        ...state.deviceProductionPlanListForOnlineDispatching.filter(
          (plan) => !deviceIds?.includes(plan.deviceId)
        )
      ];

      state.deviceProductionPlanListForOnlineDispatching = [
        ...plansFromStateWithoutNewPlans,
        ...plans
      ];
    },
    setPlansIntoDeviceProductionPlansBatchForPlanning: (
      state,
      action: PayloadAction<GetProductionPlansBatchResponse>
    ) => {
      const plans = action.payload._embedded?.productionPlanDetailDtoList;

      if (!plans || plans.length === 0) return;

      const deviceIds = plans.map(
        (device: GetDeviceProductionPlanDetailResponse) => device.deviceId
      );

      const plansFromStateWithoutNewPlans = [
        ...state.deviceProductionPlanListForPlanning.filter(
          (plan) => !deviceIds?.includes(plan.deviceId)
        )
      ];

      state.deviceProductionPlanListForPlanning = [
        ...plansFromStateWithoutNewPlans,
        ...plans
      ];
    },
    setSelectedBusinessDay: (state, action: PayloadAction<string>) => {
      state.selectedBusinessDay = action.payload;
    },
    setSseToken: (state, action: PayloadAction<any>) => {
      state.sseToken = action.payload;
    },
    setOperationCalendarList(state, action: PayloadAction<any>) {
      state.operationCalendar.calendarList = action.payload;
    },
    setOperationEventList(state, action: PayloadAction<any>) {
      state.operationCalendar.events = action.payload;
    },
    setOperationEventDetail(state, action: PayloadAction<any>) {
      state.operationCalendar.eventDetail = action.payload;
    },
    setDispatcherEtag(state, action: PayloadAction<EtagInterface>) {
      const { entity, etag } = action.payload;
      state.etag = { ...state.etag, [entity]: etag };
    },
    setIntoTimeSeriesForOnlineDispatchingBatch(
      state,
      action: PayloadAction<SetIntoTimeSeriesPayloadBatch>
    ) {
      state.timeSeriesListForOnlineDispatching = getTimeSeries(
        state.timeSeriesListForOnlineDispatching,
        action
      );
    },
    setIntoTimeSeriesForPlanningBatch(
      state,
      action: PayloadAction<SetIntoTimeSeriesPayloadBatch>
    ) {
      state.timeSeriesListForPlanning.SYSTEM = getTimeSeries(
        state.timeSeriesListForPlanning.SYSTEM,
        action,
        MonitoredResourceType.SYSTEM
      );
      state.timeSeriesListForPlanning.BALANCING_GROUP = getTimeSeries(
        state.timeSeriesListForPlanning.BALANCING_GROUP,
        action,
        MonitoredResourceType.BALANCING_GROUP
      );
      state.timeSeriesListForPlanning.DELIVERY_POINT = getTimeSeries(
        state.timeSeriesListForPlanning.DELIVERY_POINT,
        action,
        MonitoredResourceType.DELIVERY_POINT
      );
      state.timeSeriesListForPlanning.DEVICE = getTimeSeries(
        state.timeSeriesListForPlanning.DEVICE,
        action,
        MonitoredResourceType.DEVICE
      );
    },
    clearTimeSeriesForPlanning(state) {
      state.timeSeriesListForPlanning.SYSTEM = [];
      state.timeSeriesListForPlanning.BALANCING_GROUP = [];
      state.timeSeriesListForPlanning.DELIVERY_POINT = [];
      state.timeSeriesListForPlanning.DEVICE = [];
    },
    setIntoTimeSeriesForHxBatch: (
      state,
      action: PayloadAction<SetIntoTimeSeriesPayloadBatch>
    ) => {
      state.timeSeriesListForHx.SYSTEM = getTimeSeries(
        state.timeSeriesListForHx.SYSTEM,
        action,
        MonitoredResourceType.SYSTEM
      );
      state.timeSeriesListForHx.BALANCING_GROUP = getTimeSeries(
        state.timeSeriesListForHx.BALANCING_GROUP,
        action,
        MonitoredResourceType.BALANCING_GROUP
      );
      state.timeSeriesListForHx.DEVICE = getTimeSeries(
        state.timeSeriesListForHx.DEVICE,
        action,
        MonitoredResourceType.DEVICE
      );
    },
    setIntoTimeSeriesForReservedCapacityGuardingBatch: (
      state,
      action: PayloadAction<SetIntoTimeSeriesPayloadBatch>
    ) => {
      state.timeSeriesListForReservedCapacityGuarding.DELIVERY_POINT =
        getTimeSeries(
          state.timeSeriesListForReservedCapacityGuarding.DELIVERY_POINT,
          action,
          MonitoredResourceType.DELIVERY_POINT
        );
      state.timeSeriesListForReservedCapacityGuarding.DEVICE = getTimeSeries(
        state.timeSeriesListForReservedCapacityGuarding.DEVICE,
        action,
        MonitoredResourceType.DEVICE
      );
    },
    clearTimeSeriesForReservedCapacityGuarding(state) {
      state.timeSeriesListForReservedCapacityGuarding.DELIVERY_POINT = [];
      state.timeSeriesListForReservedCapacityGuarding.DEVICE = [];
    },
    setDailyDiagram: (
      state,
      action: PayloadAction<DailyDiagramInterface | undefined>
    ) => {
      state.dailyDiagram = action.payload;
    },
    setDailyDiagramList: (
      state,
      action: PayloadAction<DailyDiagramListItemInterface[] | undefined>
    ) => {
      state.dailyDiagramList = action.payload ? action.payload : [];
    },
    setMetricSpecs: (state, action: PayloadAction<any>) => {
      state.metricSpecs = action.payload;
    },
    setDispatcherDeliveryPoints(
      state,
      action: PayloadAction<FetchDeliveryPointListResponse | undefined>
    ) {
      const deliveryPointList =
        action.payload?._embedded.deliveryPointListDtoList ?? [];

      state.balancingGroup.deliveryPointList = deliveryPointList;
    },
    setTimeSeries: (state, action: PayloadAction<any>) => {
      state.timeSeries = action.payload;
    },
    setProductionPlanActivityLog: (
      state,
      action: PayloadAction<ActivityLogInterface[]>
    ) => {
      const newLogIdList = action.payload.map((log) => log.id);

      const activityLogsWithoutNewLogs = state.productionPlanActivityLog.filter(
        (log) => !newLogIdList.includes(log.id)
      );

      state.productionPlanActivityLog = [
        ...activityLogsWithoutNewLogs,
        ...action.payload
      ];
    },
    setDailyDiagramActivityLog: (
      state,
      action: PayloadAction<ActivityLogInterface[]>
    ) => {
      state.dailyDiagramActivityLog = action.payload;
    },
    setCalendarActivityLog: (
      state,
      action: PayloadAction<ActivityLogInterface[]>
    ) => {
      state.calendarActivityLog = action.payload;
    },
    setActivityLogs: (state, action: PayloadAction<ActivityLogInterface[]>) => {
      state.activityLogs = action.payload;
    },
    setIsDailyDiagramReady: (state, action: PayloadAction<boolean>) => {
      state.isReady.isDailyDiagramReady = action.payload;
    },
    setIsProductionPlanListReady: (state, action: PayloadAction<boolean>) => {
      state.isReady.isProductionPlanListReady = action.payload;
    },
    setIsDeviceMetricListReady: (state, action: PayloadAction<boolean>) => {
      state.isReady.isDeviceMetricListReady = action.payload;
    },
    setIsMetricListForBalanceGroupChartReady: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isReady.isMetricListForBalanceGroupChartReady = action.payload;
    },
    setIsMetricListForSystemValuesChartReady: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isReady.isMetricListForSystemValuesChartReady = action.payload;
    },
    clearIsDispatcherReady: (state) => {
      state.isReady = initialIsReady;
    },
    setReservedCapacityGuardingActions: (
      state,
      action: PayloadAction<ReservedCapacityGuardingAction[]>
    ) => {
      state.reservedCapacityGuardingActions = action.payload;
    },
    setIsReservedCapacityGuardingActionsReady: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isReservedCapacityGuardingActionsReady = action.payload;
    },
    setAlarms: (state, action: PayloadAction<AlarmDto[]>) => {
      state.alarms = action.payload;
    }
  }
});

export const {
  clearDispatcherState,
  setCodebooks,
  setDispatcherContextActions,
  removeDispatcherContextActions,
  setIntoDeviceProductionPlanForOnlineDispatching,
  setIntoDeviceProductionPlanForPlanning,
  setPlansIntoDeviceProductionPlansBatchForOnlineDispatching,
  setPlansIntoDeviceProductionPlansBatchForPlanning,
  setSelectedBusinessDay,
  setSseToken,
  setMetricSpecs,
  setDispatcherDeliveryPoints,
  setOperationCalendarList,
  setOperationEventList,
  setOperationEventDetail,
  setDispatcherEtag,
  setIntoTimeSeriesForOnlineDispatchingBatch,
  setIntoTimeSeriesForPlanningBatch,
  clearTimeSeriesForPlanning,
  setIntoTimeSeriesForHxBatch,
  setIntoTimeSeriesForReservedCapacityGuardingBatch,
  clearTimeSeriesForReservedCapacityGuarding,
  setDailyDiagram,
  setDailyDiagramList,
  setProductionPlanActivityLog,
  setDailyDiagramActivityLog,
  setCalendarActivityLog,
  setActivityLogs,
  setIsDailyDiagramReady,
  setIsProductionPlanListReady,
  setIsDeviceMetricListReady,
  setIsMetricListForBalanceGroupChartReady,
  setIsMetricListForSystemValuesChartReady,
  clearIsDispatcherReady,
  setReservedCapacityGuardingActions,
  setIsReservedCapacityGuardingActionsReady,
  setAlarms
} = dispatcherSlice.actions;

export const dispatcher = dispatcherSlice.reducer;
